import * as XLSX from 'xlsx';
import { rpcClient } from '@/api/rpc/client';
import type { WorkbookEditor } from './useWorkbookEditor';

const XLSX_FILE = /\.xlsx$/i;
const CSV_FILE = /\.csv$/i;
/** The largest CSV converted in the page: parsing a bigger one would stall it. */
const MAX_CSV_BYTES = 10 * 1024 * 1024;

/** Whether a Workbook can take `file` as a File: an Excel workbook, or a CSV it converts to one. */
export function isSpreadsheetFile(file: Pick<File, 'name'>): boolean {
  return XLSX_FILE.test(file.name) || CSV_FILE.test(file.name);
}

/**
 * `file` as an Excel workbook, which is all a Workbook attaches: a CSV becomes
 * one sheet of text cells (no value is reinterpreted as a number or date),
 * named as the CSV with `.xlsx` in place of `.csv`.
 */
export async function asXlsxFile(file: File): Promise<File> {
  if (!CSV_FILE.test(file.name)) return file;
  if (file.size > MAX_CSV_BYTES)
    throw new Error('CSV files must be 10 MB or smaller.');
  const parsed = XLSX.read(await file.text(), { type: 'string', raw: true });
  const bytes: ArrayBuffer = XLSX.write(parsed, {
    type: 'array',
    bookType: 'xlsx',
    // Uncompressed sheet XML can outgrow the CSV, and the upload limit.
    compression: true,
  });
  return new File([bytes], file.name.replace(CSV_FILE, '.xlsx'), {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export async function fileBase64(file: File): Promise<string> {
  if (file.size > 5 * 1024 * 1024)
    throw new Error('Spreadsheets must be 5 MB or smaller as an Excel file.');
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}
export async function uploadReference(
  editor: Pick<WorkbookEditor, 'workbookId' | 'edits'>,
  file: File,
) {
  const xlsx = await asXlsxFile(file);
  const attachment = await rpcClient.workbooks.upload({
    workbookId: editor.workbookId,
    fileName: xlsx.name,
    base64: await fileBase64(xlsx),
  });
  editor.edits.workbook.update((definition) => ({
    ...definition,
    files: [...definition.files, attachment],
  }));
  return attachment;
}
